<template>
  <SuperAdminShell>
    <div class="super-admin-page sa-dashboard">
      <DashboardHeader :is-mock="isMock" :updated-at="updatedAtLabel" />

      <div class="super-admin-page__shell sa-dashboard__shell" v-if="hasReady && !loadError">
        <div class="sa-dashboard__container">
          <section class="sa-dashboard__kpis" aria-label="Indicadores principais">
            <DashboardKpiCard
              label="Faturamento"
              :value="formatCurrencyBRL(summary.totalRevenue)"
              icon="fa-sack-dollar"
              :hint="'Operação consolidada Weeduc e Weflix'"
            />
            <DashboardKpiCard
              label="Alunos / colaboradores"
              :value="formatNumberBR(summary.totalStudents)"
              icon="fa-users"
              hint="Base ativa nas empresas cadastradas"
            />
            <DashboardKpiCard
              label="Empresas"
              :value="formatNumberBR(summary.totalCompanies)"
              icon="fa-building"
            >
              <template #hint>
                <span class="sa-dashboard__kpi-split">
                  <span><em>{{ formatNumberBR(summary.weeduc) }}</em> Weeduc</span>
                  <span><em>{{ formatNumberBR(summary.weflix) }}</em> Weflix</span>
                </span>
              </template>
            </DashboardKpiCard>
            <DashboardKpiCard
              label="Vendas"
              :value="formatNumberBR(summary.totalSales)"
              icon="fa-cart-shopping"
              :hint="`${formatNumberBR(summary.totalCourses)} cursos criados`"
            />
          </section>

          <section class="sa-dashboard__block">
            <DashboardSectionHeader title="Performance" subtitle="Evolução de faturamento e distribuição da base" />
            <div class="sa-dashboard__grid sa-dashboard__grid--8-4">
              <DashboardChartCard
                title="Tendência de faturamento"
                subtitle="Série mensal consolidada"
                emphasis
              >
                <VueApexCharts
                  type="area"
                  height="340"
                  width="100%"
                  :options="revenueChartOptions"
                  :series="revenueChartSeries"
                />
              </DashboardChartCard>

              <DashboardDistribution
                title="Distribuição das empresas"
                subtitle="Leitura imediata por sistema e tipo"
                :groups="distributionGroups"
              />
            </div>
          </section>

          <section class="sa-dashboard__block">
            <DashboardSectionHeader title="Crescimento" subtitle="Expansão da base de clientes" />
            <div class="sa-dashboard__grid sa-dashboard__grid--8-4">
              <DashboardChartCard title="Crescimento de empresas" subtitle="Empresas acumuladas no período">
                <VueApexCharts
                  type="bar"
                  height="300"
                  width="100%"
                  :options="companiesChartOptions"
                  :series="companiesChartSeries"
                />
              </DashboardChartCard>

              <DashboardBaseComposition
                :escolar="summary.escolar"
                :corporativo="summary.corporativo"
                :weflix="summary.weflix"
              />
            </div>
          </section>

          <section class="sa-dashboard__block">
            <DashboardRanking
              v-model="rankingTab"
              :top-by-revenue="metrics.topByRevenue"
              :top-by-students="metrics.topByStudents"
              :top-by-sales="metrics.topBySales"
              :top-by-courses="metrics.topByCourses"
            />
          </section>
        </div>
      </div>

      <div class="super-admin-page__shell" v-else-if="loadError">
        <div class="super-admin-page__panel sa-dashboard__state sa-dashboard__state--error" role="alert">
          <h2>Não foi possível carregar o dashboard</h2>
          <p>Tente novamente em instantes. Se o problema continuar, verifique sua conexão.</p>
          <button type="button" class="btnSave" @click="loadMetrics">Tentar novamente</button>
        </div>
      </div>

      <div class="super-admin-page__shell" v-else>
        <div class="super-admin-page__panel">
          <p class="sa-dashboard__loading">Carregando dashboard...</p>
        </div>
      </div>
    </div>
  </SuperAdminShell>
</template>

<script>
import { mapState } from 'vuex';
import SuperAdminShell from '@/Views/Weflix/SuperAdminShell.vue';
import superAdminApi from '@/utils/superAdmin';
import {
  formatCompactCurrency,
  formatCurrencyBRL,
  formatNumberBR,
  MONTH_LABELS,
} from '@/utils/superAdmin/formatters';
import DashboardHeader from '@/components/Weflix/SuperAdmin/Dashboard/DashboardHeader.vue';
import DashboardKpiCard from '@/components/Weflix/SuperAdmin/Dashboard/DashboardKpiCard.vue';
import DashboardSectionHeader from '@/components/Weflix/SuperAdmin/Dashboard/DashboardSectionHeader.vue';
import DashboardChartCard from '@/components/Weflix/SuperAdmin/Dashboard/DashboardChartCard.vue';
import DashboardDistribution from '@/components/Weflix/SuperAdmin/Dashboard/DashboardDistribution.vue';
import DashboardBaseComposition from '@/components/Weflix/SuperAdmin/Dashboard/DashboardBaseComposition.vue';
import DashboardRanking from '@/components/Weflix/SuperAdmin/Dashboard/DashboardRanking.vue';

export default {
  name: 'SuperAdminDashboard',
  components: {
    SuperAdminShell,
    DashboardHeader,
    DashboardKpiCard,
    DashboardSectionHeader,
    DashboardChartCard,
    DashboardDistribution,
    DashboardBaseComposition,
    DashboardRanking,
  },
  data() {
    return {
      hasReady: false,
      loadError: false,
      isMock: false,
      rankingTab: 'revenue',
      loadedAt: null,
      metrics: {
        summary: {},
        companiesBySystem: [],
        weeducByBusiness: [],
        topByStudents: [],
        topByRevenue: [],
        topBySales: [],
        topByCourses: [],
        revenueTrend: [],
        companiesTrend: [],
      },
    };
  },
  computed: {
    ...mapState(['token']),
    summary() {
      return this.metrics.summary || {};
    },
    updatedAtLabel() {
      if (!this.loadedAt) return '';
      return this.loadedAt.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    distributionGroups() {
      const bySystem = this.metrics.companiesBySystem || [];
      const byBusiness = this.metrics.weeducByBusiness || [];
      const systemTotal = bySystem.reduce((sum, item) => sum + Number(item.value || 0), 0);
      const businessTotal = byBusiness.reduce((sum, item) => sum + Number(item.value || 0), 0);

      return [
        {
          title: 'Por sistema',
          total: systemTotal,
          items: bySystem.map((item) => ({
            label: item.label,
            value: item.value,
            color: item.color || (item.label === 'Weflix' ? '#1AC7D0' : '#250891'),
          })),
        },
        {
          title: 'Weeduc por tipo',
          total: businessTotal,
          items: byBusiness.map((item) => ({
            label: item.label,
            value: item.value,
            color: item.color || '#7C5CFC',
          })),
        },
      ];
    },
    revenueChartSeries() {
      return [{
        name: 'Faturamento',
        data: (this.metrics.revenueTrend || []).map((item) => item.value),
      }];
    },
    revenueChartOptions() {
      return {
        chart: {
          type: 'area',
          toolbar: { show: false },
          fontFamily: 'Inter, system-ui, sans-serif',
          zoom: { enabled: false },
          animations: { enabled: true, speed: 400 },
        },
        colors: ['#250891'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2.5 },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.22,
            opacityTo: 0.02,
            stops: [0, 90, 100],
          },
        },
        markers: {
          size: 0,
          hover: { size: 5 },
        },
        xaxis: {
          categories: (this.metrics.revenueTrend || []).map((item) => MONTH_LABELS[item.month - 1]),
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: { colors: '#697386', fontSize: '12px' },
          },
        },
        yaxis: {
          labels: {
            style: { colors: '#697386', fontSize: '12px' },
            formatter: (val) => formatCompactCurrency(val),
          },
        },
        tooltip: {
          y: { formatter: (val) => formatCurrencyBRL(val) },
        },
        grid: {
          borderColor: '#edf0f5',
          strokeDashArray: 3,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: { left: 8, right: 8 },
        },
      };
    },
    companiesChartSeries() {
      return [{
        name: 'Empresas',
        data: (this.metrics.companiesTrend || []).map((item) => item.value),
      }];
    },
    companiesChartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: 'Inter, system-ui, sans-serif',
          animations: { enabled: true, speed: 400 },
        },
        colors: ['#1AC7D0'],
        plotOptions: {
          bar: {
            borderRadius: 6,
            borderRadiusApplication: 'end',
            columnWidth: '42%',
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: (this.metrics.companiesTrend || []).map((item) => MONTH_LABELS[item.month - 1]),
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: { colors: '#697386', fontSize: '12px' },
          },
        },
        yaxis: {
          tickAmount: 4,
          labels: {
            style: { colors: '#697386', fontSize: '12px' },
          },
        },
        tooltip: {
          y: { formatter: (val) => formatNumberBR(val) },
        },
        grid: {
          borderColor: '#edf0f5',
          strokeDashArray: 3,
          xaxis: { lines: { show: false } },
          padding: { left: 8, right: 8 },
        },
      };
    },
  },
  methods: {
    formatCurrencyBRL,
    formatNumberBR,
    async loadMetrics() {
      this.loadError = false;
      this.hasReady = false;
      try {
        const data = await superAdminApi.getDashboardMetrics(this.token);
        this.metrics = data;
        this.isMock = !!data.isMock;
        this.loadedAt = new Date();
        this.hasReady = true;
      } catch (error) {
        console.error('Erro ao carregar dashboard Super Admin:', error);
        this.loadError = true;
        this.hasReady = true;
      }
    },
  },
  created() {
    this.loadMetrics();
  },
};
</script>

<style scoped>
.sa-dashboard {
  --dashboard-bg: #f6f7fb;
  --dashboard-card: #ffffff;
  --dashboard-border: #e8eaf0;
  --dashboard-text: #191c24;
  --dashboard-muted: #697386;
  --dashboard-primary: #250891;
  --dashboard-secondary: #1ac7d0;
  background: var(--dashboard-bg);
}

.sa-dashboard__shell {
  background: var(--dashboard-bg);
}

.sa-dashboard__container {
  width: 100%;
  max-width: none;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.sa-dashboard__kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.15rem;
}

.sa-dashboard__kpi-split {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 1rem;
}

.sa-dashboard__kpi-split em {
  font-style: normal;
  font-weight: 700;
  color: var(--dashboard-text);
}

.sa-dashboard__block {
  min-width: 0;
}

.sa-dashboard__grid {
  display: grid;
  gap: 1.15rem;
  align-items: stretch;
}

.sa-dashboard__grid--8-4 {
  grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr);
}

.sa-dashboard__loading {
  margin: 0;
  color: var(--dashboard-muted);
}

.sa-dashboard__state {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1.5rem 0;
}

.sa-dashboard__state h2 {
  margin: 0;
  font-size: 1.25rem;
  color: var(--dashboard-text);
}

.sa-dashboard__state p {
  margin: 0;
  color: var(--dashboard-muted);
}

.sa-dashboard__state--error {
  max-width: 36rem;
}

@media (max-width: 1280px) {
  .sa-dashboard__grid--8-4 {
    grid-template-columns: minmax(0, 1.6fr) minmax(260px, 1fr);
  }
}

@media (max-width: 1024px) {
  .sa-dashboard__kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sa-dashboard__grid--8-4 {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .sa-dashboard__kpis {
    grid-template-columns: 1fr;
  }

  .sa-dashboard__container {
    gap: 1.25rem;
  }
}
</style>
